import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap, throwError } from 'rxjs';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from '@trovata/app/core/models/config.model';
import { NO_TMS_FEATURE, tmsNoAccessMessage } from '@trovata/app/features/tms/utils/tms-access-error';
import { TmsAccessService } from '@trovata/app/features/tms/services/tms-access.service';
import { TmsAvailabilityService } from '@trovata/app/features/tms/services/tms-availability.service';

/**
 * Watches every TTMS REST call and keeps {@link TmsAccessService} in step with TTMS's
 * answer: a 403 "No TTMS access" sets the refusal message, any successful response
 * clears it (e.g. once the customer is linked again). The error itself still reaches
 * the caller unchanged.
 *
 * <p>A user known to lack Trovata TMS (feature 300, {@link TmsAvailabilityService}) never
 * reaches TTMS: the request is not sent and the caller gets the same 403 shape TTMS uses,
 * with reason NO_TMS_FEATURE, so every existing error path applies unchanged.
 */
@Injectable()
export class TmsAccessInterceptor implements HttpInterceptor {
	private readonly access: TmsAccessService = inject(TmsAccessService);
	private readonly availability: TmsAvailabilityService = inject(TmsAvailabilityService);
	private readonly environment: DefaultEnvironment = inject<DefaultEnvironment>(ENVIRONMENT_TOKEN);

	intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (!this.isAtomUrl(req.url)) {
			return next.handle(req);
		}
		if (!this.availability.allowsRequests()) {
			const refusal: HttpErrorResponse = new HttpErrorResponse({
				status: 403,
				statusText: 'Forbidden',
				url: req.url,
				error: { error: 'No TTMS access', reason: NO_TMS_FEATURE },
			});
			this.access.setDenied(tmsNoAccessMessage(refusal) as string);
			return throwError(() => refusal);
		}
		return next.handle(req).pipe(
			tap({
				next: (event: HttpEvent<unknown>): void => {
					if (event instanceof HttpResponse && this.access.denied() !== null) {
						this.access.clear();
					}
				},
				error: (err: unknown): void => {
					const message: string | null = tmsNoAccessMessage(err);
					if (message !== null) {
						this.access.setDenied(message);
					}
				},
			})
		);
	}

	/** Same rule as AuthInterceptor: the configured ATOM origin, or relative /api/ when local dev proxies it. */
	private isAtomUrl(url: string): boolean {
		return this.environment.atomApiUrl ? url.startsWith(this.environment.atomApiUrl) : url.startsWith('/api/');
	}
}
